import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'underscore';
import { Tooltip } from 'react-tooltip';

import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import Dropdown from 'components/CustomizableDropdown/CustomizableDropdown';
import Collapse from 'components/Collapse/Collapse';
import { Container } from 'components/Layout/Layout';
import { getConfig } from 'functions/getConfig';
import { getCookie } from 'functions/getCookie';
import CourseFlowOverlay from 'components/CourseFlowOverlay/CourseFlowOverlay';
import {
  HeadingLabel,
  LabelAndDropdownContainer,
  ProgrammingLanguageLabel,
  ExerciseContainer,
  SyllabusContainer,
  Link,
  LevelNameContainer,
  CompletedIcon,
  ExerciseAndLevelNameContainer,
  LevelName,
  ExerciseName,
} from './styles';
const config = getConfig();

export default function CourseContent() {
  const { user } = useSelector((state) => state.auth);
  const { selectedCourse: course, inputExercise } = useSelector((state) => state.CourseLandingPageReducer);

  const { getCourse, updateFlowProgress, resetInputExerciseProgress } = useActions(actions);

  let languageOptions = [];

  for (const language in course.languages) {
    languageOptions.push({ label: course.languages[language].SimpleName, value: language });
  }
  languageOptions.push({ label: 'Any Language', value: 'any' });

  const [showFlowOverlay, setShowFlowOverlay] = useState(false);
  const [courseFlow, setCourseFlow] = useState(false);
  const [defaultInputExercise, setDefaultInputExercise] = useState({});
  const [language, setLanguage] = useState(
    getCookie(`fsCourse-${course._id}-language`) ?? course.lastUsedLanguage ?? 'python'
  );
  const [firstIncompleteLevel, setFirstIncompleteLevel] = useState({
    syllabusIndex: 0,
    moduleIndex: 0,
    levelIndex: 0,
  });

  useEffect(() => {
    let hasCourseCookies =
      getCookie(`fsCourse-${course._id}-language`) || getCookie(`fsCourse-${course._id}-levelToRun`);
    if (hasCourseCookies && course.syllabus[firstIncompleteLevel.syllabusIndex]) {
      let element = document.getElementById(firstIncompleteLevel.syllabusIndex);
      let elementPosition = element.getBoundingClientRect().top;
      let offsetPosition = elementPosition + window.scrollY - 65;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [firstIncompleteLevel, course]);

  // It finds which level is not completed of the selected language
  const updateFirstIncompleteLevel = useCallback(() => {
    if (!user._id) return;
    syllabusLoop: for (const [syllabusIndex, syllabusObject] of course.syllabus.entries()) {
      for (const [moduleIndex, module] of syllabusObject.modules.entries()) {
        let isExerciseCompleted = module.exercise.isCompletedInAnyLanguage;
        if (language === 'any' && isExerciseCompleted) continue;
        else if (language === 'any' && !isExerciseCompleted) {
          let levelObjectWithLanguage = {};
          for (const language in course.languages) {
            for (const [levelIndex, level] of module?.exercise?.languagesCompleted[language].entries()) {
              if (levelObjectWithLanguage[levelIndex]) continue;
              if (level.isCompleted) levelObjectWithLanguage[levelIndex] = true;
              else levelObjectWithLanguage[levelIndex] = false;
            }
          }
          for (const lockedLevel in levelObjectWithLanguage) {
            if (!levelObjectWithLanguage[lockedLevel]) {
              setFirstIncompleteLevel({
                syllabusIndex,
                moduleIndex,
                levelIndex: Number(lockedLevel),
              });
              break syllabusLoop;
            }
          }
        } else {
          for (const [levelIndex, level] of module?.exercise?.languagesCompleted[language]?.entries()) {
            let isCompleted = level.isCompleted;
            if (!isCompleted) {
              setFirstIncompleteLevel({
                syllabusIndex,
                moduleIndex,
                levelIndex,
              });
              break syllabusLoop;
            }
          }
        }
      }
    }
  }, [course, language, user._id]);

  useEffect(() => {
    updateFirstIncompleteLevel();
  }, [updateFirstIncompleteLevel]);

  const getToolTip = (languagesCompleted, levelIndex) => {
    let languages = '';
    for (const language in course.languages) {
      let isCompleted = languagesCompleted[language][levelIndex].isCompleted;
      if (isCompleted) {
        if (languages) languages += `, ${course.languages[language].SimpleName}`;
        else languages = course.languages[language].SimpleName;
      }
    }
    return languages ? `Level is completed in ${languages}` : '';
  };

  const checkAndInitializeFlow = (event, exerciseId, levelIndex, testImpl, inputExercise) => {
    localStorage.setItem(`fsEditor-exercise-${exerciseId}-levelToRun`, String(levelIndex));
    document.cookie = `fsCourse-${course._id}-levelToRun=${levelIndex + 1}; Domain=${
      config.COOKIE
    }; Max-Age=100; Path=/; SameSite=Lax`;
    event.stopPropagation();
    if (testImpl.parsedFlow?.[levelIndex]) {
      setCourseFlow({
        levelIndexStart: levelIndex,
        courseId: course._id,
        testImpl: testImpl,
        language: language === 'any' ? 'python' : language,
      });
      setDefaultInputExercise(inputExercise);
      setShowFlowOverlay(true);
    } else {
      window.location.href = `/exercise/${exerciseId}?courseId=${course._id}`;
    }
  };

  const renderLink = (
    exerciseId,
    syllabusIndex,
    levelNames,
    exerciseName,
    moduleName,
    isCompletedInAnyLanguage,
    languagesCompleted,
    testImpl,
    moduleIndex,
    inputExercise
  ) => {
    let isCompleted = isCompletedInAnyLanguage
      ? true
      : _.every(_.pluck(levelNames, 'isCompleted'), (levelCompleted) => levelCompleted === true);

    const checkHighlightedIndex = (levelIndex) => {
      return (
        syllabusIndex === firstIncompleteLevel.syllabusIndex &&
        moduleIndex === firstIncompleteLevel.moduleIndex &&
        levelIndex === firstIncompleteLevel.levelIndex
      );
    };

    return (
      <Link onClick={(event) => checkAndInitializeFlow(event, exerciseId, 0, testImpl, inputExercise)}>
        <CompletedIcon>
          <i className={`fa-regular ${isCompleted ? 'fa-check-circle' : 'fa-circle'} fa-lg pt-1 mr-2`} />
        </CompletedIcon>
        <ExerciseAndLevelNameContainer>
          <ExerciseName>{moduleName ? `${moduleName} - ${exerciseName}` : exerciseName}</ExerciseName>
          <LevelNameContainer>
            {levelNames?.map((level, levelIndex) => {
              let isLevelCompleted = isCompleted ? true : level.isCompleted;
              if (language === 'any' && !isLevelCompleted) {
                for (const language in course.languages) {
                  isLevelCompleted = languagesCompleted[language][levelIndex].isCompleted;
                  if (isLevelCompleted) break;
                }
              }
              return (
                <LevelName
                  key={`${exerciseName}${level.levelName}${levelIndex}`}
                  isCompleted={isLevelCompleted}
                  isHighlighted={checkHighlightedIndex(levelIndex)}
                  data-tooltip-id={`levelTip${exerciseName}${level.levelName}${levelIndex}`}
                  data-tooltip-content={
                    checkHighlightedIndex(levelIndex)
                      ? syllabusIndex === 0 && moduleIndex === 0 && levelIndex === 0
                        ? 'Start here'
                        : 'Continue here'
                      : getToolTip(languagesCompleted, levelIndex)
                  }
                  data-tooltip-variant="info"
                  onClick={(event) => checkAndInitializeFlow(event, exerciseId, levelIndex, testImpl, inputExercise)}
                >
                  <Tooltip
                    id={`levelTip${exerciseName}${level.levelName}${levelIndex}`}
                    place="top"
                    className="custom-tooltip"
                  />
                  {isLevelCompleted && <i className="fa-regular fa-circle-check" aria-hidden="true"></i>}
                  {` `}
                  {level.levelName}
                </LevelName>
              );
            })}
          </LevelNameContainer>
        </ExerciseAndLevelNameContainer>
      </Link>
    );
  };

  const renderSyllabus = () =>
    course?.syllabus?.map((lesson, syllabusIndex) => (
      <Collapse
        key={lesson.name}
        id={syllabusIndex}
        title={lesson.name}
        description={lesson.description}
        isCollapsed={!!lesson.active}
        isCollapsable={false}
        isHighlighted={syllabusIndex === firstIncompleteLevel.syllabusIndex}
      >
        {lesson?.modules?.map((module, moduleIndex) => (
          <ExerciseContainer key={moduleIndex}>
            {renderLink(
              module.exercise.id,
              syllabusIndex,
              module.exercise.languagesCompleted[language === 'any' ? 'python' : language],
              module.exercise.name,
              module.name,
              language === 'any' ? module.exercise.isCompletedInAnyLanguage : false,
              module.exercise.languagesCompleted,
              module.exercise.testImpl,
              moduleIndex,
              module.exercise.inputExercise
            )}
          </ExerciseContainer>
        ))}
      </Collapse>
    ));

  return (
    <Container column usePadding="0 0 30px 0">
      <HeadingLabel>Syllabus</HeadingLabel>
      <LabelAndDropdownContainer>
        <ProgrammingLanguageLabel>Select Programming Language:</ProgrammingLanguageLabel>
        <Dropdown
          width="150px"
          options={languageOptions}
          value={language}
          onChange={(selected) => {
            setLanguage(selected.value);
            document.cookie = `fsCourse-${course._id}-language=${selected.value}; Domain=${config.COOKIE}; Max-Age=604800; Path=/; SameSite=Lax`;
          }}
        />
      </LabelAndDropdownContainer>
      <SyllabusContainer>{renderSyllabus()}</SyllabusContainer>
      {showFlowOverlay && (
        <CourseFlowOverlay
          courseFlow={{ ...courseFlow }}
          inputExercise={
            inputExercise._id
              ? inputExercise
              : defaultInputExercise._id
                ? defaultInputExercise
                : JSON.parse(
                    localStorage.getItem(`fs-flow-progress-${courseFlow.testImpl.test}`) ??
                      JSON.stringify({ levelData: {} })
                  )
          }
          updateFlowProgress={(data) => updateFlowProgress(user._id, data)}
          close={() => {
            updateFirstIncompleteLevel();
            window.customPixiComponentForFlow = {};
            resetInputExerciseProgress();
            getCourse(course._id);
            setShowFlowOverlay(false);
          }}
        />
      )}
    </Container>
  );
}
